<!-- Main Content Area -->
<div class="confirm-page">
  <div class="confirm-page__content">

    <!-- Left: Customer information Form -->
    <div class="confirm-page__main">
      <div class="confirm-page__card">
        <h2 class="confirm-page__title">Customer information</h2>
        <p class="confirm-page__subtitle">Provide all mandatory fields</p>

        <hr class="confirm-page__divider" />

        <form class="confirm-page__form" [formGroup]="form" (ngSubmit)="onScheduleAppointment()">
          <!-- Name row -->
          <div class="confirm-page__form-row">
            <div class="confirm-page__field">
              <label class="confirm-page__label">First name <span class="confirm-page__required">*</span></label>
              <mat-form-field class="confirm-page__field-full" appearance="outline">
                <input matInput placeholder="John" formControlName="firstName" />
                @if (form.get('firstName').hasError('required') || form.get('firstName').hasError('pattern')) {
                  <mat-error>This field is required</mat-error>
                }
              </mat-form-field>
            </div>
            <div class="confirm-page__field">
              <label class="confirm-page__label">Last name <span class="confirm-page__required">*</span></label>
              <mat-form-field class="confirm-page__field-full" appearance="outline">
                <input matInput placeholder="Doe" formControlName="lastName" />
                @if (form.get('lastName').hasError('required') || form.get('lastName').hasError('pattern')) {
                  <mat-error>This field is required</mat-error>
                }
              </mat-form-field>
            </div>
          </div>

          <!-- Email -->
          <div class="confirm-page__form-group">
            <label class="confirm-page__label">Email address @if (isEmailRequired) {<span class="confirm-page__required">*</span>}</label>
            <mat-form-field class="confirm-page__field-full" appearance="outline">
              <input matInput placeholder="john@example.com" type="email" formControlName="email" />
              @if (form.get('email').hasError('required')) {
                <mat-error>This field is required</mat-error>
              } @else if (form.get('email').hasError('invalidEmail')) {
                <mat-error>Email is invalid</mat-error>
              }
            </mat-form-field>
          </div>

          <!-- Additional guests: hide for SMS-only bookings since guests are
               only useful when email notifications are sent. -->
          @if (isEmailRequired && !showGuestEmails) {
            <div class="confirm-page__add-guests" (click)="toggleGuestEmails()">
              + Add additional guests
            </div>
          }
          @if (isEmailRequired && showGuestEmails) {
            <div class="confirm-page__form-group confirm-page__guests-section">
              <div class="confirm-page__guests-row">
                <mat-form-field class="confirm-page__guests-field" appearance="outline">
                  <mat-chip-grid #chipGrid aria-label="Guest email list">
                    @for (email of guestEmails; track email) {
                      <mat-chip-row (removed)="removeGuestEmail(email)">
                        {{ email }}
                        <button matChipRemove aria-label="Remove email">
                          <mat-icon>cancel</mat-icon>
                        </button>
                      </mat-chip-row>
                    }
                  </mat-chip-grid>
                  <input
                    #guestEmailInput
                    matChipInput
                    type="email"
                    [placeholder]="guestEmails.length ? '' : 'Enter email'"
                    [matChipInputFor]="chipGrid"
                    [matChipInputSeparatorKeyCodes]="separatorKeyCodes"
                    [matChipInputAddOnBlur]="true"
                    (matChipInputTokenEnd)="addGuestEmail($event.value)"
                  />
                </mat-form-field>
                <button
                  type="button"
                  mat-icon-button
                  class="confirm-page__clear-all"
                  aria-label="Clear all and close"
                  (click)="clearAllGuestEmails(); toggleGuestEmails()"
                >
                  <mat-icon>cancel</mat-icon>
                </button>
              </div>
              <span class="confirm-page__helper-text">Enter additional guests email id here</span>
            </div>
          }

          <!-- Phone -->
          <div class="confirm-page__form-group">
            <label class="confirm-page__label">Phone number @if (isPhoneRequired) {<span class="confirm-page__required">*</span>}</label>
            <app-phone-input
              class="confirm-page__field-full"
              [formControl]="phoneNumberControl"
              label=""
              [required]="isPhoneRequired"
            ></app-phone-input>
            @if (showSmsNotice) {
              <div class="confirm-page__sms-notice" role="note">
                <mat-icon class="confirm-page__sms-notice-icon">info</mat-icon>
                <span i18n="@@BOOK.SMS_CONSENT_DISCLOSURE">
                  {{ 'BOOK.SMS_CONSENT_DISCLOSURE' | translate }}
                </span>
              </div>
            }
          </div>

          <!-- Location (only for in-person client site events) -->
          @if (showLocationField) {
            <div class="confirm-page__form-group">
              <label class="confirm-page__label">Location <span class="confirm-page__required">*</span></label>
              <mat-form-field class="confirm-page__field-full" appearance="outline">
                <input matInput placeholder="Add your location" formControlName="location" />
                @if (form.get('location').hasError('required') || form.get('location').hasError('pattern')) {
                  <mat-error>This field is required</mat-error>
                }
              </mat-form-field>
              @if (locationInstructions) {
                <div class="confirm-page__location-instructions">
                  <span class="confirm-page__location-instructions-label">Location Guidelines:</span>
                  {{ locationInstructions }}
                </div>
              }
            </div>
          }

          <!-- Custom Questions -->
          @if (customFormFields.length > 0) {
            <app-dynamic-form
              class="confirm-page__field-full"
              [fields]="customFormFields"
            ></app-dynamic-form>
          }

          <!-- Additional comments -->
          <div class="confirm-page__form-group">
            <label class="confirm-page__label">Additional comments</label>
            <mat-form-field class="confirm-page__field-full" appearance="outline">
              <textarea matInput rows="4" placeholder="Any special requests or notes for your appointment..." formControlName="comment"></textarea>
            </mat-form-field>
          </div>

          @if (isPhoneRequired) {
          <p class="confirm-page__terms-links">
            <a href="https://businessapp.io/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            <span class="confirm-page__terms-separator">&bull;</span>
            <a href="https://businessapp.io/terms-of-service/" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          </p>
          }

          @if (submissionError()) {
            <div class="confirm-page__error-banner" role="alert">
              <mat-icon class="confirm-page__error-icon">error_outline</mat-icon>
              <span>{{ submissionError() }}</span>
            </div>
          }
        </form>
      </div>
    </div>

    <!-- Right: Summary Cart -->
    <div class="confirm-page__cart-wrapper">
      <app-booking-cart
        [items]="cartItems()"
        headerTitle="Summary"
        [scheduledDate]="scheduledDate()"
        [scheduledTime]="scheduledTime()"
        [showMakeChangesLink]="true"
        [readOnly]="cartItems().length <= 1"
        ctaLabel="Schedule appointment"
        mobileCtaLabel="Book"
        [ctaDisabled]="form.invalid || customFieldsInvalid || submitting"
        [showBackButton]="true"
        backLabel="Back"
        (ctaClick)="onScheduleAppointment()"
        (backClick)="onBack()"
        (removeItem)="onRemoveCartItem($event)"
        (makeChangesClick)="onMakeChanges()"
      ></app-booking-cart>
    </div>
  </div>
</div>
